import BoxInfo from '@/components/boxInfo/boxInfo';
import FileUpload from '@/components/fileUpload';
import FormValidate, { BodyForm } from '@/components/formValidate';
import GridGroup from '@/components/gridGroup';
import ToastStatusGlobal from '@/components/toastStatusGlobal';
import { CADASTRAL_POSITION_ALLOWED_EXTENSIONS } from '@/modules/cadastralPosition/entities/cadastralPosition.const';
import { CADASTRAL_POSITION_UPLOAD_STEPS } from '@/modules/cadastralPosition/entities/cadastralPosition.enum';
import useCadastralPositionUpload from '@/modules/cadastralPosition/hooks/useCadastralPositionUpload';
import { Company } from '@/modules/company/entities/interfaces/company';
import SelectCompany from '@/modules/company/views/selectCompany/selectCompany';
import DialogConfirmBeneficiaryWithoutCPF from '@/modules/move/components/dialogConfirmBeneficiaryWithoutCPF/dialogConfirmBeneficiaryWithoutCPF';
import { useHistoryNavigator } from '@/navigation';
import { MovesValidationError } from '@/services/files/endpoints/moves';
import ICONS from '@/shared/entities/constants/icons';
import { downloadFileLink } from '@/utils/common';
import React from 'react';
import { Button, Loader } from 'vkit/lib/components';

interface UploadFormProps {
  setCurrentStep: (step: CADASTRAL_POSITION_UPLOAD_STEPS) => void;
  setErrors: (errors: MovesValidationError) => void;
  validationError: MovesValidationError;
}

const UploadForm: React.FC<UploadFormProps> = ({ setCurrentStep, setErrors, validationError }) => {
  const navigate = useHistoryNavigator();
  const { toast } = ToastStatusGlobal();

  const {
    formValues,
    loading,
    setFormValues,
    handleSubmit,
    handleError,
    showDialogConfirmBeneficiaryWithoutCPF,
    setShowDialogConfirmBeneficiaryWithoutCPF,
  } = useCadastralPositionUpload({
    setCurrentStep,
    setErrors,
  });

  const downloadTemplateFile = (): void => {
    const cadastralPositonTemplate = process.env.REACT_APP_API_CADASTRAL_POSITION_TEMPLATE_FILE_URL;
    if (cadastralPositonTemplate) {
      downloadFileLink(cadastralPositonTemplate, 'posicao-cadastral-template.csv');
    } else {
      toast('Oops!', 'Falha ao baixar arquivo.', 'error');
    }
  };

  const getFields = ({ onChange, onBlur, useErrors, useValues }: BodyForm) => (
    <GridGroup
      body={[
        {
          default: 100,
          middle: 100,
          component: (
            <SelectCompany
              required
              label='Grupo Empresarial'
              selectedCompanyId={useValues.companyId}
              onChangeSelectedCompany={(_value: string, company: Company) => {
                setFormValues((current) => ({
                  ...current,
                  companyGroupId: company.companyGroupId!,
                }));
              }}
              errorMessage={useErrors.companyId}
              onlyStipulators
            />
          ),
        },

        {
          default: 100,
          middle: 100,
          component: (
            <FileUpload
              label='Selecione a planilha correspondente'
              name='files'
              allowedExtensions={CADASTRAL_POSITION_ALLOWED_EXTENSIONS}
              onChange={(name: string, value: File) => {
                onChange(name, value);
                onBlur(name, value);
              }}
              formValidatorErrors={useErrors}
            />
          ),
        },
      ]}
    />
  );

  return (
    <>
      {loading && <Loader full />}

      <DialogConfirmBeneficiaryWithoutCPF
        open={showDialogConfirmBeneficiaryWithoutCPF}
        setOpen={setShowDialogConfirmBeneficiaryWithoutCPF}
        errorDetails={validationError.details}
        onConfirm={(cpfField) => {
          handleSubmit({
            skipRequiredValidations: [cpfField],
          });
          setShowDialogConfirmBeneficiaryWithoutCPF(false);
        }}
      />

      <BoxInfo
        title='Upload de posição cadastral'
        icon={ICONS.CADASTRAL_POSITION}
        header={
          <Button
            bold
            invertColor
            onClick={downloadTemplateFile}
            label='Download do arquivo modelo'
          />
        }>
        <FormValidate
          resource={formValues}
          onChangeForm={setFormValues}
          onBack={navigate.goBack}
          onSubmit={handleSubmit}
          onError={handleError}
          fields={{
            companyGroupId: ['required'],
            files: ['required'],
          }}
          body={getFields}
          buttons={{
            submit: {
              text: 'Concluir',
              loading,
            },
            back: {
              text: 'Voltar',
            },
          }}
        />
      </BoxInfo>
    </>
  );
};

export default UploadForm;
